import {useEffect, useRef} from 'react';

export type OnTabOutsideOptions = {
  trigger?: 'once' | 'onceAndReset';
  disabled?: boolean;
};

export const useOnTabOutside = (
  ref: React.RefObject<HTMLElement | null>,
  handler: (event: Event) => void,
  opts: OnTabOutsideOptions = {},
) => {
  const hasBeenTriggeredOnceRef = useRef(false);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const el = ref.current;

      if (!el || opts.disabled || event.key !== 'Tab') {
        return;
      }

      const hasTabbedInside =
        el.contains(event?.target as Node) ||
        el.contains(document.activeElement);

      if (hasTabbedInside) {
        if (opts.trigger === 'onceAndReset') {
          // On inner tab reset 'hasBeenTriggeredOnce' so the handler
          // can be called the next time after the user clicked outside.
          hasBeenTriggeredOnceRef.current = false;
        }
        return;
      }

      if (!opts.trigger) {
        handler(event);
        return;
      }

      // for trigger 'once' | 'onceAndReset'
      if (!hasBeenTriggeredOnceRef.current) {
        hasBeenTriggeredOnceRef.current = true;
        handler(event);
      }
    };

    document.addEventListener('keyup', listener);

    return () => {
      document.removeEventListener('keyup', listener);
    };
  }, [ref, handler, opts.trigger, hasBeenTriggeredOnceRef]);
};
